import React, { CSSProperties, ReactElement } from 'react';
import { ColorFrom, Feature } from '@tencent/workbench';
import { IBorderFeature } from './border-interface';
import { FONT_BORDER_KEY } from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import { BorderType, IBorderInfo } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { FormItemName, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import { MULTI } from '@src/js/constant';
import { canvasToPt, ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import { AngleFrom, NumberInputFrom } from '@tencent/wb-ext-form';
import { getColorRGB } from '@src/slide/infrastructure/module/independent/color/transform';
import type { IColorProps } from '@framework/workbench/drawer/ui/border/border-feature';
import { alphaToOpacity, opacityToAlpha } from '@framework/workbench/drawer/utils';
import {
    BorderStyle,
    DirectList,
    getConfigByRect,
    getLinearDirection,
    getRectDirection,
    ShapeDirectionList,
} from '@editor/src/js/constants';
import { IFillGradualValues } from '@framework/workbench/drawer/ui/text/fill/fill-interface';
import { GradientType, IGradFill } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { LINEAR_GRADIENT, RADIAL_GRADIENT } from '@framework/workbench/drawer/ui/text/fill/config';
import { FillFeature } from '@framework/workbench/drawer/ui/text/fill/fill-feature';
import { TdwDrawerConstMap, tdwDrawerReport } from '@framework/workbench/drawer/report';
import BaseDrawerFeature from '@framework/workbench/common/feature/base-drawer-feature';
import _cloneDeep from 'lodash/cloneDeep';
import i18n from '@i18next/i18n';
import {
    DirectOptions,
    LinearDirectOptions,
    RECT_GRADIENT,
    RectDirectOptions,
    SHAPE_GRADIENT,
    ShapeDirectOptions,
} from '@framework/workbench/drawer/ui/fill/constants';
import { getDrawerColorOptions } from '@framework/workbench/drawer/utils/color-options';
import { ColorSelectButton } from '@framework/workbench/drawer/components/color-select-button';
import {
    TDWVer5Type,
    TDWVer6Type,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { handleInputBlurInitAndReset } from '@src/slide/application/service/drawer-input-manager/drawer-handler';
import ColorPickerContainer from '@src/js/common/ark-extensions/dc-components/color-picker/color-picker-container';
import { ColorPickerType, ColorSourceType } from '@src/js/common/ark-extensions/dc-components/color-picker/type';
import Dropdown from '@tencent/dui/lib/components/Dropdown';
import { STSchemeColorVal } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';
import userLocalStorage from '@src/slide/infrastructure/module/independent/local-storage';
import { NO_COLOR } from '@src/slide/infrastructure/module/independent/color/constant';
import {
    GradualDirection,
    GradualSetting,
    IDirectionOption,
    OperationType,
    RadioItem,
    BorderLine,
    DrawerBorderType,
    BorderTypes,
    IDrawerBorderType,
    IGradualPoint as IDrawerGradualPoint,
} from '@tencent/drawing-drawer-components';
import { IGradualPoint } from '@framework/workbench/drawer/ui/fill/gradual-fill/gradual-fill-feature.interface';

// 纯色填充默认设置
export const DEFAULT_SOLID_BORDER = {
    color: '#2f528f',
    style: 'solid',
    width: ptToCanvas(0.75),
};
// 渐变填充默认值
const DEFAULT_GRADUAL_ANGLE = 135;
const DEFAULT_GRADUAL_START_COLOR = '#FFF2CC';
const DEFAULT_GRADUAL_END_COLOR = '#FFB700';
const DEFAULT_GRADUAL_POINT_ARR = [
    {
        id: GradualSetting.startPoint,
        left: 0,
        bgColor: new TDColor({
            rgbaStr: DEFAULT_GRADUAL_START_COLOR,
        }),
        percent: 0,
    },
    {
        id: GradualSetting.endPoint,
        left: GradualSetting.gradientContentWidth - GradualSetting.pointWidth,
        bgColor: new TDColor({
            rgbaStr: DEFAULT_GRADUAL_END_COLOR,
        }),
        percent: 100,
    },
];

const RECENT_FONT_BORDER_COLOR_KEY = 'recent_font_border_color_key';
const RECENT_FONT_BORDER_COLOR_KEY_THEME = 'recent_font_border_color_key_theme';

@Feature()
export class BorderFeature extends BaseDrawerFeature implements IBorderFeature {
    // 纯色填充 - 对应的临时编辑数据
    public borderSolidValues: ITDColor = new TDColor({
        rgbaStr: DEFAULT_SOLID_BORDER.color,
    });
    // 渐变填充 - 对应的临时编辑数据
    public borderGradualValues: IFillGradualValues = {};
    // 公共线宽 - 各类型公用的临时编辑数据
    public width = DEFAULT_SOLID_BORDER.width;
    // 公共线类型 - 各类型公用的临时编辑数据
    public style = DEFAULT_SOLID_BORDER.style;
    // 预览数据
    public borderValuesPreview?: IBorderInfo;
    // 从model获取出来的数据
    private borderInfoInDrawer: IBorderInfo = {};
    // 是否包含多中文字边框样式
    private isMultiDifferent = false;
    private isChangeGradualDirection = false;

    public constructor() {
        super();
        this.addInitPropsCallback(INPUT_PROPS_CALLBACK_KEY.TEXT_BORDER);
    }

    /**
     * 更新当前页面的边框数据源
     * 并生成当前边框类型对应的临时编辑数据
     */
    public initPropsCallback = (): void => {
        this.borderInfoInDrawer = { ...this.getBorderInfoFromModel() };
        this.width = this.borderInfoInDrawer.width ?? this.width ?? DEFAULT_SOLID_BORDER.width;
        this.style = this.borderInfoInDrawer.style ?? this.style ?? DEFAULT_SOLID_BORDER.style;
        this.initSolidValues();
        this.initGradualValues();
    };

    /**
     * 渲染边框列表组件
     */
    public renderBorderLine(): ReactElement {
        return (
            <BorderLine
                key={FormItemName.FONT_BORDER_LINE}
                style={this.borderValuesPreview?.style || this.style}
                handleBorderStyleClick={value => this.handleBorderStyleClick(value)}
                handleBorderMenuMouseEnter={this.handleBorderMenuMouseEnter}
                handleBorderMenuMouseLeave={this.handleBorderMenuMouseLeave}
                tooltip={i18n.t('线条类型')}
                dropdownStyle={{
                    marginLeft: 'auto',
                }}
                buttonStyle={{
                    width: 118,
                }}
                innerLineStyle={{
                    marginLeft: 12,
                    width: 80,
                }}
            />);
    }

    /**
     * 渐变填充 - 渲染编辑组件
     */
    public renderGradualFill = (): ReactElement => <GradualSetting
        pointArray={this.getPointArray()}
        activePointId={this.borderGradualValues.activePointId}
        updatePoint={this.handleGradualUpdatePoint}
        getDropContent={() => <ColorPickerContainer
            colorPickerType={ColorPickerType.StandardPC}
            onColorChange={this.handlePaletteGradualColorChange}
            selectedTdColor={this.borderGradualValues.activeColor}
        />}
    />;

    /**
     * 获取当前选中的边框类型
     */
    public getCheckedFillType = (): IDrawerBorderType => {
        if (this.borderInfoInDrawer.gradFill) {
            return DrawerBorderType.GRAD_FILL;
        }
        if (this.borderInfoInDrawer.color) {
            return DrawerBorderType.SOLID_FILL;
        }
        if (this.borderInfoInDrawer.noFill) {
            return DrawerBorderType.NO_FILL;
        }
        if (Object.keys(this.borderInfoInDrawer).length === 0 && !this.isMultiDifferent) {
            // 没有设置项且不是多种混合则兜底成无填充
            return DrawerBorderType.NO_FILL;
        }
        // 最后兜底成未选择（目前只有多种混合才会走到这里）
        return DrawerBorderType.NO_SELECT;
    };

    /**
     * 切换边框类型
     * @param type
     */
    public handleBorderTypeChange = (type: IDrawerBorderType): void => {
        if (this.getCheckedFillType() === type) {
            return;
        }
        const values = { border: {} };
        if (type === DrawerBorderType.NO_FILL) {
            values.border = { noFill: 1 };
            this.updateProps(ActionType.FONT_BORDER, values);
        } else if (type === DrawerBorderType.SOLID_FILL) {
            values.border = {
                color: this.borderSolidValues.srgb_color,
                tdColor: this.borderSolidValues,
                width: this.width,
                style: this.style,
            };
            this.updateProps(ActionType.FONT_BORDER, values);
        } else if (type === DrawerBorderType.GRAD_FILL) {
            this.borderGradualValues = {
                activePointId: DEFAULT_GRADUAL_POINT_ARR[0].id,
                activeColor: DEFAULT_GRADUAL_POINT_ARR[0].bgColor,
                curGradualType: LINEAR_GRADIENT,
                curDirect: DirectList[0],
                curAngle: DEFAULT_GRADUAL_ANGLE,
                pointArray: _cloneDeep(DEFAULT_GRADUAL_POINT_ARR),
            };
            this.submitGradualValues();
        }
        // 上报
        const ver5 = {
            [DrawerBorderType.NO_FILL]: 1,
            [DrawerBorderType.SOLID_FILL]: 2,
            [DrawerBorderType.GRAD_FILL]: 3,
        }[type];
        this.tdwDrawerReport(FormItemName.FONT_BORDER_SELECT, { ver5 });
    };

    /**
     * 改变边框宽度
     * @param value
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.TABLE_BORDER)
    public handleBorderWidthChange(value: number): void {
        const values = { border: { ...this.borderInfoInDrawer } };
        values.border.width = value;
        const isOnlyChangeUI = this.isInputOnlyChangeUI();
        if (isOnlyChangeUI) return;
        this.updateProps(ActionType.FONT_BORDER, values, isOnlyChangeUI);
        // 上报
        this.tdwDrawerReport(FormItemName.FONT_BORDER_WIDTH, { ver5: value }, isOnlyChangeUI);
    }

    /**
     * 色板事件绑定
     * @param value
     * @param from
     * @param mouseMoveType
     */
    public changeBorderColor = (value: ITDColor, from: any): void => {
        this.handleColorChange({
            color: value,
            onlyChangeUI: false,
            from,
        });
        this.setRecentFontBorder(value);
    };

    public setRecentFontBorder = (tdColor: ITDColor) => {
        if (!tdColor) {
            localStorage.setItem(RECENT_FONT_BORDER_COLOR_KEY, NO_COLOR);
            localStorage.removeItem(RECENT_FONT_BORDER_COLOR_KEY);
            return;
        }
        localStorage.setItem(RECENT_FONT_BORDER_COLOR_KEY, tdColor.getColorWithNoAlpha());
        if (STSchemeColorVal[tdColor.colorValue]) {
            localStorage.setItem(RECENT_FONT_BORDER_COLOR_KEY_THEME, JSON.stringify({
                themeKey: tdColor.colorValue as STSchemeColorVal,
                lumMod: tdColor.lumMod,
                lumOff: tdColor.lumOff,
                srgbColor: tdColor.srgb_color,
            }));
        } else {
            localStorage.removeItem(RECENT_FONT_BORDER_COLOR_KEY_THEME);
        }
    };

    public getRecentColor = (): { tdColor?: TDColor } => {
        const colorStr = localStorage.getItem(RECENT_FONT_BORDER_COLOR_KEY);
        const tdColorObj = localStorage.getItem(RECENT_FONT_BORDER_COLOR_KEY_THEME);
        let tdColor: TDColor | undefined = undefined;

        // 如果是无填充，则直接返回undefined
        if (colorStr === NO_COLOR) {
            return { tdColor: undefined };
        }

        // else兼容历史数据
        if (tdColorObj) {
            try {
                const tdColorObjParse = JSON.parse(tdColorObj);
                const { themeKey, lumMod, lumOff, srgbColor } = tdColorObjParse;
                tdColor = new TDColor({
                    rgbaStr: srgbColor,
                    themeKey,
                    lumMod,
                    lumOff,
                });
            } catch (e) {
                tdColor = new TDColor({
                    rgbaStr: DEFAULT_SOLID_BORDER.color,
                });
            }
        } else if (colorStr) {
            tdColor = new TDColor({
                rgbaStr: colorStr,
            });
        } else {
            tdColor = new TDColor({
                rgbaStr: DEFAULT_SOLID_BORDER.color,
            });
        }
        return {
            tdColor,
        };
    };

    /**
     * 切换边框颜色
     * @param props
     */
    public handleColorChange = (props: IColorProps): void => {
        const { color: tdColor } = props;
        const { onlyChangeUI, from, needReport } = props;
        let values;
        if (onlyChangeUI) {
            this.updatePreviewState(true);
            this.borderValuesPreview = { color: tdColor.srgb_color, tdColor };
            values = { border: { ...this.borderValuesPreview, width: this.width, style: this.style } };
        } else {
            this.updatePreviewState(false);
            values = { border: { color: tdColor.srgb_color, tdColor, width: this.width, style: this.style } };
        }
        this.updateProps(ActionType.FONT_BORDER, values);
        // 上报
        if (!onlyChangeUI && (needReport !== false)) {
            this.getColorPickerManager()?.colorPickerTdwReport({
                color: tdColor.srgb_color,
                colorPickerFrom: TDWVer6Type.COLOR_PICKER_DRAWER,
                colorGroupFrom: from,
                actionType: TDWVer5Type.COLOR_PICKER_BORDER,
            });
        }
    };

    /**
     * 获取color的透明度
     * @param color
     */
    public getOpacityFromColor(): number {
        return alphaToOpacity(this.borderSolidValues.alpha);
    }

    /**
     * 通过透明度和颜色拿到新的颜色
     * @param opacity
     * @param color
     */
    public getNewColorFromOpacity(opacity: number, color: string): string {
        const alpha = opacityToAlpha(opacity);
        const rgb = getColorRGB(color);
        return `rgb(${rgb[0]},${rgb[1]},${rgb[2]},${alpha})`;
    }

    /**
     * 切换透明度
     * @param value
     * @param onlyChangeUI
     * @param from
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.TABLE_BORDER)
    public handleOpacityChange(opacity: number, dragEnd: boolean, from: NumberInputFrom): void {
        if (!this.borderSolidValues) {
            return;
        }
        const onlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        if (onlyChangeUI) {
            this.borderSolidValues.updateOpacity(opacity);
            return;
        }
        this.handleColorChange({ color: this.borderSolidValues, onlyChangeUI, from, needReport: false });
        // 上报
        this.tdwDrawerReportNumberInput(
            FormItemName.FONT_BORDER_OPACITY,
            { ver6: this.borderSolidValues.srgb_color }, from,
        );
    }

    /**
     * 渐变填充 - 切换类型
     * @param type
     */
    public handleGradualTypeChange = (type: string): void => {
        if (this.borderGradualValues.curGradualType !== type) {
            this.borderGradualValues.curGradualType = type;
            this.borderGradualValues.curDirect = this.getFillDirectionOptions()[0];
        }
        this.submitGradualValues();
        // 上报数据
        const ver5 = type === LINEAR_GRADIENT ? 1 : 2;
        this.tdwDrawerReport(TdwDrawerConstMap.fontBorderGradualFillLinerOrRadial, { ver5 });
    };

    /**
     * 渐变填充 - 切换线性角度
     * @param value
     * @param from
     * @param isDragging
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.TABLE_BORDER)
    public handleGradualAngleChange(value: number, from?: AngleFrom, dragEnd?: boolean): void {
        this.borderGradualValues.curAngle = value;
        const isOnlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        if (isOnlyChangeUI) {
            return;
        }
        this.submitGradualValues();
        // 上报数据
        this.tdwDrawerReportAngle(FormItemName.FONT_BORDER_GRADUAL_ANGLE, from, value);
    }

    /**
     * 色盘hover入
     * @param value
     * @private
     */
    public handleColorEnter = (value: string): void => {
        this.handleColorChange({ color: new TDColor({ rgbaStr: value }), onlyChangeUI: true, from: ColorFrom.NONE });
    };

    /**
     * 色盘离开
     * @private
     */
    public handlePaletteLeave = (): void => {
        if (this.getTextEditorStateService().isTextPreview) {
            this.callbackByActionType(ActionType.FONT_REFRESH);
        }
        this.updatePreviewState(false);
    };

    /**
     * 切换无边框
     */
    public handleNoBorderClick = (): void => {
        this.updatePreviewState(false);
        const values = { border: { noFill: 1 } };
        this.updateProps(ActionType.FONT_BORDER, values);
        // 上报
        this.tdwDrawerReport(FormItemName.FONT_BORDER_PRESET, { ver5: 1 });
    };


    /**
     * 渐变填充 - 切换径向方向
     * @param value
     */
    public handleGradualDirectionChange = (value: { id: string, rect: any, title: string }): void => {
        this.borderGradualValues.curDirect = value;
        this.isChangeGradualDirection = true;
        this.submitGradualValues();
        // 上报数据
        this.tdwDrawerReport(FormItemName.FONT_BORDER_GRADUAL_DIRECTION, { ver5: value?.title });
        this.isChangeGradualDirection = false;
    };

    /**
     * 渐变填充 - 透明度变更
     * @param opacity
     * @param from
     * @param onlyChangeUI
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.TABLE_BORDER)
    public handleGradualOpacityChange(opacity: number, from?: NumberInputFrom, dragEnd?: boolean): void {
        const newColor = this.borderGradualValues.activeColor || DEFAULT_GRADUAL_POINT_ARR[0].bgColor;
        newColor?.updateOpacity(opacity);
        this.borderGradualValues.activeColor = newColor;
        const curPoint = this.borderGradualValues.pointArray.find(e => e.id === this.borderGradualValues.activePointId);
        if (curPoint) {
            curPoint.bgColor = newColor;
            const onlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
            if (onlyChangeUI) {
                return;
            }
            this.submitGradualValues();
            // 上报数据
            this.tdwDrawerReportNumberInput(FormItemName.FONT_BORDER_GRADUAL_OPACITY, { ver6: opacity }, from);
        }
    }

    public getFillDirectionOptions() {
        if (this.borderGradualValues.curGradualType === LINEAR_GRADIENT) {
            return LinearDirectOptions;
        }
        if (this.borderGradualValues.curGradualType === RECT_GRADIENT) {
            return RectDirectOptions;
        }
        if (this.borderGradualValues.curGradualType === SHAPE_GRADIENT) {
            return ShapeDirectOptions;
        }
        return DirectOptions;
    }

    public shouldAngleDisabled(): boolean {
        const type = this.getCheckedFillType();
        return !(type ===  DrawerBorderType.GRAD_FILL
            && this.borderGradualValues.curGradualType === LINEAR_GRADIENT);
    }

    public getBorderColorPicker = () => <Dropdown.Keep>
        <ColorPickerContainer
            onColorHover={this.handleColorEnter}
            onMouseLeave={this.handlePaletteLeave}
            selectedTdColor={this.borderSolidValues || '#000000'}
            colorPickerType={ColorPickerType.StandardPC}
            onColorChange={this.changeBorderColor}
        />
    </Dropdown.Keep>;

    public getBorderColorPickerNoKeep = () => <ColorPickerContainer
        onMouseLeave={this.handlePaletteLeave}
        selectedTdColor={this.borderSolidValues || '#000000'}
        colorPickerType={ColorPickerType.StandardPC}
        onColorChange={this.changeBorderColor}
    />;

    public getColorSelectButton() {
        const option =  getDrawerColorOptions((color, from) => {
            this.handleColorChange({ color, onlyChangeUI: false, from });
        }, this.getMenuManager());

        return <ColorSelectButton
            onColorHover={this.handleColorEnter}
            onMouseLeave={this.handlePaletteLeave}
            onColorChange={this.changeBorderColor}
            value={this.borderSolidValues || '#000000'}
            paletteButtonsStyle={{
                width: 116,
                marginLeft: 'auto',
            }}
            colorBoxStyle={{
                width: 88,
            }}
        />;
    }

    public renderBorderContent() {
        this.initPropsCallback();
        return <RadioItem
            radioOptions={BorderTypes}
            onChange={this.handleBorderTypeChange}
            value={this.getCheckedFillType()}
        />;
    }

    public renderGradualDirection = () => {
        const { curGradualType } = this.borderGradualValues;
        let path = GradientType.Linear;
        if (curGradualType === RADIAL_GRADIENT) {
            path = GradientType.Radial;
        } else if (curGradualType === RECT_GRADIENT) {
            path = GradientType.Rect;
        } else if (curGradualType === SHAPE_GRADIENT) {
            path = GradientType.Shape;
        }
        return <GradualDirection
            path={path}
            directionOptions={this.getFillDirectionOptions()}
            tooltip={i18n.t('方向')}
            pointArray={this.getPointArray()}
            handleChange={this.handleGradualDirectionChange}
            getCurrentDirection={() => this.borderGradualValues.curDirect as IDirectionOption}
        ></GradualDirection>;
    };

    private getPointArray = (): IDrawerGradualPoint[] => this.borderGradualValues.pointArray;

    /**
     * 渐变填充 - 渐变点切换
     * @param id
     * @param pointArray
     * @param onlyChangeUI
     * @param operation
     */
    private handleGradualUpdatePoint = (
        id: string,
        pointArray: IDrawerGradualPoint[],
        onlyChangeUI: boolean,
        operation?: string,
    ): void => {
        pointArray = pointArray.map((item) => {
            const bgColor = item.bgColor instanceof TDColor ? item.bgColor
                : new TDColor({ rgbaStr: item.bgColor.srgbColor });
            return {
                ...item,
                bgColor,
            };
        }) as IGradualPoint[];
        // 切换渐变点时，强刷一下，否则透明度不更新
        if (this.borderGradualValues.activePointId !== id) {
            this.formService.$forceUpdate();
        }
        this.borderGradualValues.activePointId = id || this.borderGradualValues.activePointId;
        this.borderGradualValues.activeColor = (pointArray as IGradualPoint[])
            .find(e => e.id === this.borderGradualValues.activePointId)?.bgColor
            || this.borderGradualValues.activeColor;
        this.borderGradualValues.pointArray = pointArray || this.borderGradualValues.pointArray;
        if (onlyChangeUI) {
            return;
        }
        this.submitGradualValues();
        if (operation) {
            const formItemKey = {
                [OperationType.ADD]: TdwDrawerConstMap.fontBorderGradualAddPoint,
                [OperationType.REMOVE]: TdwDrawerConstMap.fontBorderGradualRemovePoint,
                [OperationType.REVERT]: TdwDrawerConstMap.fontBorderGradualRevertPoint,
            }[operation];
            tdwDrawerReport(formItemKey);
        }
    };

    /**
     * 渐变填充 - 色板切换
     * @param color
     * @param from
     */
    // eslint-disable-next-line max-len
    private handlePaletteGradualColorChange = (color: ITDColor, from: ColorSourceType): void => {
        const curPoint = this.borderGradualValues.pointArray.find(e => e.id === this.borderGradualValues.activePointId);
        if (curPoint) {
            this.borderGradualValues.activeColor = color;
            curPoint.bgColor = color;
            this.submitGradualValues();
            // 上报数据
            let ver5 = 1;
            if (from === ColorSourceType.Custom) {
                ver5 = 3;
            } else if (from === ColorSourceType.Recent) {
                ver5 = 2;
            }
            this.tdwDrawerReport(TdwDrawerConstMap.fontBorderGradualPointColorChange, { ver5 });
        }
    };

    private submitGradualValues(): void {
        const values = {
            border: {
                gradFill: this.getGradualValues(),
                width: this.width,
                style: this.style,
            },
        };
        this.updateProps(ActionType.FONT_BORDER, values);
        this.callbackByActionType(ActionType.FONT_REFRESH);
    }

    /**
     * 切换边框详细类型
     * @param value
     * @private
     */
    private handleBorderStyleClick(value: { style: string, width?: number }): void {
        this.updatePreviewState(false);
        const values = { border: { ...this.borderInfoInDrawer } };
        values.border.style = value.style;
        if (value.width) {
            values.border.width = ptToCanvas(value.width);
        }
        this.updateProps(ActionType.FONT_BORDER, values);
        // 上报
        const ver5 = (BorderStyle.findIndex(e => e.style === value.style) + 1);
        this.tdwDrawerReport(FormItemName.FONT_BORDER_LINE, { ver5 });
    }

    /**
     * 边框详细类型hover入
     * @param value
     */
    private handleBorderMenuMouseEnter = (value): void => {
        this.updatePreviewState(true);
        this.borderValuesPreview = { ...this.borderInfoInDrawer };
        this.borderValuesPreview.style = value.style;
        if (value.width) {
            this.borderValuesPreview.width = ptToCanvas(value.width);
        }
        const values = { border: this.borderValuesPreview };
        this.updateProps(ActionType.FONT_BORDER, values);
    };

    /**
     * 预设hover出
     */
    private handleBorderMenuMouseLeave = (): void => {
        if (this.getTextEditorStateService().isTextPreview) {
            this.callbackByActionType(ActionType.FONT_REFRESH);
        }
        this.updatePreviewState(false);
    };

    /**
     * 更新全局文本预览状态（如果是退出预览态则同时清空预览数据）
     * @param isPreview
     */
    private updatePreviewState = (isPreview: boolean): void => {
        this.getTextEditorStateService().isTextPreview = isPreview;
        if (!isPreview) {
            this.borderValuesPreview = undefined;
        }
    };

    /**
     * 初始化编辑纯色填充时的临时数据
     */
    private initSolidValues = (): void => {
        if (this.getCheckedFillType() !== DrawerBorderType.SOLID_FILL || !this.borderInfoInDrawer.tdColor) {
            this.borderSolidValues = new TDColor({
                rgbaStr: DEFAULT_SOLID_BORDER.color,
            });
            return;
        }
        this.borderSolidValues = this.borderInfoInDrawer.tdColor;
    };

    /**
     * 初始化编辑渐变填充时的临时数据
     */
    private initGradualValues = (): void => {
        const gradualData = this.borderInfoInDrawer[BorderType.GradualFill];
        if (!gradualData) {
            this.borderGradualValues = {};
            return;
        }
        const { curAngle, curGradualType, curDirect } = this.processGradualData(gradualData);
        // 渐变点
        let pointArray = _cloneDeep(DEFAULT_GRADUAL_POINT_ARR);
        let { activePointId, activeColor } = this.borderGradualValues;
        const gsList = gradualData?.gsList;
        if (gsList?.length) {
            // 新的pointArray
            pointArray = gsList.map((item, index) => FillFeature.transformPoint(index, gradualData, item));
            // 找到当前选中的渐进点index
            let curIndex = (this.borderGradualValues.pointArray || []).findIndex(e => e.id === activePointId);
            curIndex = curIndex === - 1 ? 0 : curIndex;
            // 转成成新的id
            activePointId = pointArray[curIndex].id;
            activeColor = pointArray.find(e => e.id === activePointId)?.bgColor || this.borderGradualValues.activeColor;
        }
        this.borderGradualValues = {
            ...this.borderGradualValues,
            pointArray,
            activePointId,
            activeColor,
            curAngle,
            curGradualType,
            curDirect,
        };
    };

    private processGradualData(gradualData: IGradFill) {
        // 渐变类型、角度（线性）、方向（径向）
        let { curAngle, curGradualType, curDirect } = this.borderGradualValues;
        if (gradualData?.pathGradient) {
            const pathGrad = gradualData.pathGradient;
            if (pathGrad.path === 'linear') {
                curGradualType = LINEAR_GRADIENT;
                if (gradualData?.lin) {
                    curAngle = gradualData.lin.ang;
                }
                curDirect = getLinearDirection(curAngle ?? 0) as any;
            } else if (pathGrad.path === GradientType.Rect) {
                curGradualType = RECT_GRADIENT;
                curDirect = getRectDirection(pathGrad.fillToRect) as any;
            } else if (pathGrad.path === GradientType.Shape) {
                curGradualType = SHAPE_GRADIENT;
                curDirect = ShapeDirectionList[0] as any;
            } else {
                curGradualType = RADIAL_GRADIENT;
                curDirect = getConfigByRect(pathGrad.fillToRect);
                curAngle = undefined;
            }
        }
        return { curAngle, curGradualType, curDirect };
    }

    /**
     * 从临时编辑数据整合最新的渐变填充数据
     */
    private getGradualValues = (): IGradFill => {
        const values: any = {};
        values.gsList = this.borderGradualValues.pointArray.map((item) => {
            let color;
            if (typeof item.bgColor === 'string') {
                color = { srgb_color: item.bgColor };
            } else {
                color = item.bgColor;
            }
            return  { pos: item.percent, color, id: '' };
        });
        if (this.borderGradualValues.curGradualType === LINEAR_GRADIENT) {
            // 线性渐变
            const angle = this.isChangeGradualDirection
                ? this.borderGradualValues.curDirect?.angle : this.borderGradualValues.curAngle;
            values.lin = { ang: angle ?? DEFAULT_GRADUAL_ANGLE, scaled: 1 };
            values.pathGradient = { path: GradientType.Linear };
        } else {
            // 其他渐变
            let path = GradientType.Radial;
            if (this.borderGradualValues.curGradualType === RECT_GRADIENT) {
                path = GradientType.Rect;
            } else if (this.borderGradualValues.curGradualType === SHAPE_GRADIENT) {
                path = GradientType.Shape;
            }
            const curDirect = this.borderGradualValues.curDirect || DirectList[0];
            const fillToRect = Object.assign({}, curDirect.rect);
            values.pathGradient = { path, fillToRect };
            values.tileRect = Object.assign({}, curDirect.tileRect);
        }
        return values;
    };

    /**
     * 从model获取最新的文本边框数据
     * @private
     */
    private getBorderInfoFromModel(): IBorderInfo {
        let fontBorder = this.getItemValue(FONT_BORDER_KEY);
        const { tdColor } = fontBorder || {};
        if (fontBorder === MULTI) {
            // 包含多种边框类型
            this.isMultiDifferent = true;
            fontBorder = undefined;
        } else {
            this.isMultiDifferent = false;
        }
        const res = { ...fontBorder } || {};
        if (tdColor) {
            res.tdColor = tdColor;
        }
        return res;
    }
}
